/*
  Page Table of Contents web component.

  Scans the configured content container for h2 elements, generates anchor IDs
  from heading text, builds a navigable link list, and highlights the current
  section via IntersectionObserver scrollspy.

  Modes:
    - 'auto': show only if heading count >= threshold (default 3)
    - 'always': always show when at least one heading exists
    - 'off': never show

  Attributes:
    - data-mode: 'auto' | 'always' | 'off'
    - data-threshold: minimum heading count for 'auto' mode
    - data-content: CSS selector for the content container to scan
*/

class PageToc extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'auto';
    this.threshold = parseInt(this.dataset.threshold || '3', 10);
    this.contentSelector = this.dataset.content || '.rte';

    if (this.mode === 'off') {
      this.hidden = true;
      return;
    }

    const content = document.querySelector(this.contentSelector);
    if (!content) {
      this.hidden = true;
      return;
    }

    const headings = Array.from(content.querySelectorAll('h2'));

    if (headings.length === 0) {
      this.hidden = true;
      return;
    }

    if (this.mode === 'auto' && headings.length < this.threshold) {
      this.hidden = true;
      return;
    }

    this.assignIds(headings);
    this.buildList(headings);
    this.setupScrollspy(headings);
    this.setupCollapsible();
  }

  setupCollapsible() {
    const details = this.querySelector('.page-toc__details');
    if (!details) return;

    const mq = window.matchMedia('(min-width: 990px)');

    const sync = () => {
      if (mq.matches) {
        details.setAttribute('open', '');
      } else {
        details.removeAttribute('open');
      }
    };

    sync();
    mq.addEventListener('change', sync);

    this.querySelectorAll('.page-toc__link').forEach((link) => {
      link.addEventListener('click', () => {
        if (!mq.matches) {
          details.removeAttribute('open');
        }
      });
    });
  }

  assignIds(headings) {
    const seen = new Set();
    headings.forEach((heading, index) => {
      if (!heading.id) {
        let slug = this.slugify(heading.textContent) || `heading-${index + 1}`;
        let unique = slug;
        let counter = 2;
        while (seen.has(unique) || document.getElementById(unique)) {
          unique = `${slug}-${counter++}`;
        }
        heading.id = unique;
      }
      seen.add(heading.id);
    });
  }

  buildList(headings) {
    const list = document.createElement('ul');
    list.className = 'page-toc__list';

    headings.forEach((heading) => {
      const li = document.createElement('li');
      li.className = 'page-toc__item';

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.className = 'page-toc__link';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const target = document.getElementById(heading.id);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', `#${heading.id}`);
      });

      li.appendChild(link);
      list.appendChild(li);
    });

    const container = this.querySelector('.page-toc__container');
    if (container) {
      container.appendChild(list);
    }
  }

  setupScrollspy(headings) {
    const links = Array.from(this.querySelectorAll('.page-toc__link'));
    if (links.length === 0) return;

    // Mobil har trängre layout - första rubriken hamnar lätt i active-zonen
    // direkt vid pageload. Skjuter ner zonen så användaren måste scrolla
    // ner till rubriken innan den markeras som aktiv.
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const rootMargin = isMobile
      ? '-35% 0px -55% 0px'
      : '-15% 0px -70% 0px';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            links.forEach((l) => l.classList.remove('is-active'));
            const active = this.querySelector(`a[href="#${entry.target.id}"]`);
            if (active) active.classList.add('is-active');
          }
        });
      },
      { rootMargin }
    );

    headings.forEach((heading) => observer.observe(heading));
  }

  slugify(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[åä]/g, 'a')
      .replace(/[ö]/g, 'o')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  }
}

if (!customElements.get('page-toc')) {
  customElements.define('page-toc', PageToc);
}
